import { useEffect, useRef } from "react";

/**
 * B71 FIX-3: re-esegue `fn` quando la tab del browser torna in focus o visibile.
 * Throttled a 5 secondi per evitare di chiamare l'API a ogni piccolo focus event.
 *
 * Uso:
 *   const load = () => axios.get(...)
 *   useRefetchOnFocus(load);
 */
export function useRefetchOnFocus(fn, { throttleMs = 5000 } = {}) {
  const lastRunRef = useRef(0);
  const fnRef = useRef(fn);
  // Mantiene fn aggiornato senza riattaccare i listener
  useEffect(() => { fnRef.current = fn; }, [fn]);

  useEffect(() => {
    const maybeRun = () => {
      const now = Date.now();
      if (now - lastRunRef.current < throttleMs) return;
      lastRunRef.current = now;
      try { fnRef.current && fnRef.current(); } catch { /* ignore */ }
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") maybeRun();
    };
    window.addEventListener("focus", maybeRun);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("focus", maybeRun);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [throttleMs]);
}

export default useRefetchOnFocus;
